/**
 * Admin-mediated support affordances shared by the customer (`/app`) and vendor (`/vendor`)
 * booking views. `BookingSupportButton` lets either party ask admin to recover a fallen-through
 * walker/trainer session; `MySupportRequests` shows the outcome and — for a trainer reschedule —
 * an "Agree" action on the admin-proposed slot.
 */
import { useState } from 'react';
import { LifeBuoy, Loader2, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { use_my_support_requests, use_support_mutations } from '@/features/booking-support/hooks';
import type { Side } from '@/features/booking-support/api';
import type { SupportRequest, SupportRequestStatus } from '@/features/booking-support/types';

const ELIGIBLE_MODULES = ['WALKER', 'TRAINER'];
const ELIGIBLE_STATUSES = ['AWAITING_VENDOR', 'CONFIRMED', 'NO_SHOW'];

export function booking_support_eligible(module_type: string, status: string): boolean {
  return ELIGIBLE_MODULES.includes(module_type) && ELIGIBLE_STATUSES.includes(status);
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

const STATUS_TONE: Record<SupportRequestStatus, StatusTone> = {
  OPEN: 'warning',
  PROPOSED: 'info',
  RESOLVED: 'success',
  REFUNDED: 'success',
  CANCELLED: 'neutral',
};

/** One-click "Request admin help" for an eligible booking. `side` = 'user' | 'vendor'. */
export function BookingSupportButton({
  side,
  booking,
}: {
  side: Side;
  booking: { id: string; module_type: string; status: string };
}) {
  const { open } = use_support_mutations(side);
  const [done, set_done] = useState(false);
  if (!booking_support_eligible(booking.module_type, booking.status)) return null;

  const label = booking.module_type === 'WALKER' ? 'Reassign via admin' : 'Reschedule via admin';
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      disabled={open.isPending || done}
      onClick={() => open.mutate({ booking_id: booking.id }, { onSuccess: () => set_done(true) })}
      data-testid={`support-request-${booking.id}`}
    >
      {open.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LifeBuoy className="h-4 w-4" />}
      {done ? 'Admin notified' : label}
    </Button>
  );
}

function outcome_text(r: SupportRequest): string {
  if (r.status === 'OPEN') return 'Waiting for admin to help.';
  if (r.status === 'PROPOSED') return `Proposed new time: ${r.proposed_slot_start ? fmt(r.proposed_slot_start) : '—'}`;
  if (r.status === 'RESOLVED') return r.kind === 'WALKER_REASSIGN' ? 'Reassigned to another walker.' : 'Rescheduled to the agreed time.';
  if (r.status === 'REFUNDED') return `Session refunded${r.refund_amount_inr ? ` (₹${r.refund_amount_inr})` : ''}.`;
  return 'Closed.';
}

/** A card listing the caller's own support requests, with an Agree action on proposed slots. */
export function MySupportRequests({ side }: { side: Side }) {
  const q = use_my_support_requests(side);
  const { agree } = use_support_mutations(side);
  const rows = (q.data ?? []).filter((r) => r.status === 'OPEN' || r.status === 'PROPOSED');
  if (rows.length === 0) return null;

  // For a trainer reschedule, this side still needs to agree if its own flag is false.
  const needs_my_agreement = (r: SupportRequest) =>
    r.status === 'PROPOSED' && (side === 'vendor' ? !r.trainer_agreed : !r.user_agreed);

  return (
    <div
      className="flex flex-col gap-3 rounded-card border-2 border-brand-outline-variant/20 bg-card p-4"
      data-testid="my-support-requests"
    >
      <div className="flex items-center gap-2">
        <LifeBuoy className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-bold">Admin support</h3>
      </div>
      <ul className="flex flex-col gap-3">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex flex-col gap-2 rounded-input border border-brand-outline-variant/20 p-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`support-row-${r.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusPill tone={STATUS_TONE[r.status]}>{r.status.toLowerCase()}</StatusPill>
                <span className="text-sm font-medium">
                  {r.module_type === 'WALKER' ? 'Walk' : 'Training session'} · {fmt(r.booking.slot_start)}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{outcome_text(r)}</p>
            </div>
            {needs_my_agreement(r) && (
              <Button
                variant="gradient"
                size="sm"
                className="gap-1.5 self-start sm:self-auto"
                disabled={agree.isPending}
                onClick={() => agree.mutate(r.booking_id)}
                data-testid={`support-agree-${r.id}`}
              >
                {agree.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                Agree to time
              </Button>
            )}
            {r.status === 'PROPOSED' && !needs_my_agreement(r) && (
              <span className="self-start text-xs text-muted-foreground sm:self-auto">Waiting for the other party…</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
